import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Loader } from '@src/shared/ui/components/Loader';
import { useAssets } from '@src/shared/ui/hooks/useAssets';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { Carousel, Slide } from '@uhg-abyss/web/ui/Carousel';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { SelectInputMulti } from '@uhg-abyss/web/ui/SelectInputMulti';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Timeline } from '@uhg-abyss/web/ui/Timeline';
import { chunk, isEmpty, isUndefined } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import {
  addFilteredEvents,
  getAddedEvents,
  getRemovedEvents,
  handleMatchedEvents,
  updateTimelinesAndRemovedEvents,
} from './includes/functions';
import { Styles } from './includes/styles';

/**
 * EventTimeline
 *
 * Displays a timeline of events.
 *
 * @param riskRecord
 * @returns {Element}
 * @constructor
 */
export function EventTimeline({ riskRecord }) {
  const [value, setValue] = useState([]);
  const [filteredData, setfFilteredData] = useState({});
  const [removedEvents, setRemovedEvents] = useState({});
  const eid = riskRecord?.eid;

  const { assets, isLoadingAssets } = useAssets([
    {
      apiKey: 'RiskRecord',
      queryKey: 'ListTimelineEventTypes',
    },
    {
      apiKey: 'RiskRecord',
      payload: { eid },
      queryKey: 'GetRiskRecordTimeline',
    },
  ]);

  const eventTypes = assets?.ListTimelineEventTypes?.data;
  const data = assets?.GetRiskRecordTimeline?.data;

  /**
   * Array of possible event types on the timeline
   */
  const enabledEventTypes = useMemo(() => {
    if (isUndefined(data?.timelines)) {
      return [];
    }

    const events = Object.values(data.timelines)
      .flat()
      .filter((event) => {
        return !isEmpty(event);
      });

    return Array.from(new Set(events));
  }, [data]);

  /**
   * getEventIcon
   *
   * Returns the icon based on the event type.
   *
   * @param eventType
   * @returns {string}
   */
  const getEventIcon = (eventType) => {
    const icons = {
      ACTION_PATH_ADD: 'create_new_folder',
      FLIP: 'change_circle',
      MERGE: 'merge',
      REMEDIATION: 'flag',
      RISK_CHANGE: 'warning',
      SPLIT: 'call_split',
    };

    return icons[eventType] || 'help_outline';
  };

  /**
   * getEventLabel
   *
   * Returns the label for the event type.
   *
   * @param eventType
   * @returns {*|string}
   */
  const getEventLabel = (eventType) => {
    const event = eventTypes?.find((theEvent) => {
      return theEvent.codeId === eventType;
    });
    return event ? event.codeDesc : '';
  };

  /**
   * eventTypeOptions
   *
   * An array of options for the event types select input.
   *
   * @type {{isDisabled, label: *|string, value: *}[]}
   */
  const eventTypeOptions = useMemo(() => {
    return eventTypes?.map((eventType) => {
      return {
        isDisabled: !enabledEventTypes.includes(eventType.codeId),
        label: getEventLabel(eventType.codeId),
        value: eventType.codeId,
      };
    });
  }, [eventTypes, enabledEventTypes]);

  /**
   * updateRemovedEventsState
   *
   * Updates the state of removed events based on the filtered data and events to remove.
   *
   * @param eventCodesToRemove
   * @param filteredTimelineData
   * @param setRemovedEventsState
   */
  const updateRemovedEventsState = (eventCodesToRemove, filteredTimelineData, setRemovedEventsState) => {
    setRemovedEventsState((previousRemovedEventsMap) => {
      const updatedRemovedEventsMap = { ...previousRemovedEventsMap };
      Object.entries(filteredTimelineData.timelines).forEach(([date, eventCodesForDate]) => {
        handleMatchedEvents(updatedRemovedEventsMap, date, eventCodesForDate, eventCodesToRemove);
      });
      return updatedRemovedEventsMap;
    });
  };

  /**
   * updateFilteredTimelineState
   *
   * Updates the filtered data state by removing specified events from the timelines.
   *
   * @param eventCodesToRemove
   * @param setFilteredTimelineState
   */
  const updateFilteredTimelineState = (eventCodesToRemove, setFilteredTimelineState) => {
    setFilteredTimelineState((previousFilteredTimelineData) => {
      const updatedTimelines = Object.entries(previousFilteredTimelineData.timelines).reduce(
        (filteredTimelines, [date, eventCodesForDate]) => {
          return addFilteredEvents(filteredTimelines, date, eventCodesForDate, eventCodesToRemove);
        },
        {}
      );
      return { timelines: updatedTimelines };
    });
  };

  /**
   * processRemovedEvents
   *
   * Processes the removed events by updating the removed events state and filtered data state.
   *
   * @param eventCodesToRemove
   * @param filteredTimelineData
   * @param setRemovedEventsState
   * @param setFilteredTimelineState
   */
  const processRemovedEvents = (
    eventCodesToRemove,
    filteredTimelineData,
    setRemovedEventsState,
    setFilteredTimelineState
  ) => {
    if (eventCodesToRemove.length === 0) return;
    updateRemovedEventsState(eventCodesToRemove, filteredTimelineData, setRemovedEventsState);
    updateFilteredTimelineState(eventCodesToRemove, setFilteredTimelineState);
  };

  /**
   * processAddedEvents
   *
   * Processes the added events by updating the timelines and removed events.
   *
   * @param eventCodesToAdd
   * @param previousRemovedEventsMap
   * @param previousFilteredTimelineData
   * @param setFilteredTimelineState
   * @param setRemovedEventsState
   */
  const processAddedEvents = (
    eventCodesToAdd,
    previousRemovedEventsMap,
    previousFilteredTimelineData,
    setFilteredTimelineState,
    setRemovedEventsState
  ) => {
    if (eventCodesToAdd.length === 0) return;
    const { sortedTimelines, updatedRemovedEvents } = updateTimelinesAndRemovedEvents(
      eventCodesToAdd,
      previousRemovedEventsMap,
      previousFilteredTimelineData
    );
    setFilteredTimelineState({ timelines: sortedTimelines });
    setRemovedEventsState(updatedRemovedEvents);
  };

  /**
   * handleChange
   *
   * Handles the change event for the select input, updating the value and processing added/removed events.
   *
   * @param selectedEventTypeCodes
   */
  const handleChange = (selectedEventTypeCodes) => {
    setValue(selectedEventTypeCodes);
    const removedEventTypeCodes = getRemovedEvents(value, selectedEventTypeCodes);
    const addedEventTypeCodes = getAddedEvents(value, selectedEventTypeCodes);
    processRemovedEvents(removedEventTypeCodes, filteredData, setRemovedEvents, setfFilteredData);
    processAddedEvents(addedEventTypeCodes, removedEvents, filteredData, setfFilteredData, setRemovedEvents);
  };

  /**
   * Set default value to all enabled event types
   */
  useEffect(() => {
    if (!isEmpty(enabledEventTypes)) {
      setValue(enabledEventTypes);
    }
  }, [enabledEventTypes]);

  /**
   * Set data to a filterable state
   */
  useEffect(() => {
    if (!isUndefined(data)) {
      setfFilteredData(data);
    }
  }, [data]);

  /**
   * renderSlide
   *
   * Renders a single slide of the timeline.
   *
   * @param theTimelineItems
   * @returns {Element}
   */
  const renderSlide = (theTimelineItems = []) => {
    return (
      <Slide>
        <Slide.Container>
          <Flex alignItems="center" className="eventTimelineFlex">
            <Timeline
              centeredItems
              className={theTimelineItems
                .map((item, index) => {
                  return isEmpty(item?.eventDate) ? `hide-${index}` : '';
                })
                .join(' ')}
              horizontal
            >
              {theTimelineItems?.map((timelineItem, timelineItemIndex) => {
                let bullet = null;

                if (!isEmpty(timelineItem?.eventDate)) {
                  bullet = (
                    <Layout.Group>
                      {timelineItem?.events?.map((event) => {
                        return (
                          <IconSymbol
                            icon={getEventIcon(event)}
                            key={`${timelineItem?.eventDate}-${event}-${eventTypes?.[event]?.icon}`}
                            size="24px"
                            variant="outlined"
                          />
                        );
                      })}
                    </Layout.Group>
                  );
                }

                return (
                  <Timeline.Item
                    bullet={bullet}
                    key={`${timelineItemIndex}-${timelineItem?.eventDate}`}
                    title={(timelineItem?.eventDate && dayjs(timelineItem?.eventDate).format('MMM DD, YYYY')) || ''}
                  >
                    {timelineItem?.events?.map((event, eventIndex) => {
                      return (
                        <Layout.Stack alignLayout="center" key={`${eventIndex}-${timelineItem?.eventDate}-${event}`}>
                          <Text>{getEventLabel(event)}</Text>
                        </Layout.Stack>
                      );
                    })}
                  </Timeline.Item>
                );
              })}
            </Timeline>
          </Flex>
        </Slide.Container>
      </Slide>
    );
  };

  /**
   * The timeline data
   *
   * An array of slides, each containing a set of events grouped by date.
   *
   * @type {*[]|Element[]}
   */
  const slides = useMemo(() => {
    let theSlides = [];

    if (isUndefined(filteredData?.timelines)) {
      return theSlides;
    }

    const timelines = Object.keys(filteredData?.timelines).map((eventDate) => {
      const events = filteredData?.timelines[eventDate];
      return {
        eventDate,
        events,
      };
    });

    const theTimeline = chunk(timelines, 5);

    theSlides = theTimeline.map((timelineItems) => {
      const theTimelineItems = [...timelineItems];

      if (theTimelineItems.length < 5) {
        for (let i = theTimelineItems.length; i < 5; i++) {
          theTimelineItems.push({
            eventDate: '',
            events: [''],
          });
        }
      }

      return renderSlide(theTimelineItems);
    });

    return theSlides;
  }, [filteredData, value, eventTypes]);

  return (
    <ErrorHandler location="src/widgets/RiskRecords/EventTimeline/EventTimeline.jsx">
      <Styles>
        <Grid>
          {isUndefined(data) || isLoadingAssets ? (
            <Grid.Col
              span={{
                xs: '100%',
              }}
            >
              <Loader height="100%" width="100%" />
            </Grid.Col>
          ) : (
            <Grid.Col
              className="eventTimeline"
              span={{
                xs: '100%',
              }}
            >
              <SelectInputMulti
                hideLabel
                label="Event Types"
                onChange={handleChange}
                options={eventTypeOptions}
                placeholder="Select Event Types"
                selectAll
                value={value}
                width="40%"
              />
              {!isEmpty(slides) ? (
                <Carousel minimal noLoop slides={slides} slidesPerView={1} />
              ) : (
                <Alert
                  style={{ marginTop: 'var(--abyss-space-lg)' }}
                  title="There are no events matching the selected event types."
                  variant="info"
                />
              )}
            </Grid.Col>
          )}
        </Grid>
      </Styles>
    </ErrorHandler>
  );
}
